import { createRouter, createWebHistory } from 'vue-router'
import { useUserStore } from '../stores/user'

// Import components
import Landing from '../views/Landing.vue'
import Catalogue from '../views/Catalogue.vue'
import ProductDetail from '../views/ProductDetail.vue'
import Cart from '../views/Cart.vue'
import MyOrders from '../views/MyOrders.vue'
import OrderDetails from '../views/OrderDetails.vue'
import AdminLayoutWrapper from '../views/admin/AdminLayout.vue'
import UserLayoutWrapper from '../views/UserLayoutWrapper.vue'
import AdminDashboard from '../views/admin/Dashboard.vue'
import AdminUsers from '../views/admin/Users.vue'
import AdminProducts from '../views/admin/Products.vue'
import AdminLabels from '../views/admin/Labels.vue'
import AdminPendingOrders from '../views/admin/PendingOrders.vue'
import AdminWorkshopJobs from '../views/admin/WorkshopJobs.vue'
import AdminInvites from '../views/admin/Invites.vue'
import StaffOrders from '../views/staff/Orders.vue'
import WorkshopLayout from '../views/workshop/WorkshopLayout.vue'
import WorkshopJobs from '../views/workshop/Jobs.vue'
import InviteRegistration from '../views/InviteRegistration.vue'
import Profile from '../views/Profile.vue'


const routes = [
    // Public routes
    {
        path: '/',
        name: 'Landing',
        component: Landing,
        meta: { hideNavigation: true }
    },
    {
        path: '/p/:sku',
        name: 'ProductDetail',
        component: ProductDetail,
        props: true
    },
    {
        path: '/secret/:secretKey',
        name: 'SecretProductDetail',
        component: ProductDetail,
        props: true,
        meta: { hideNavigation: true }
    },
    {
        path: '/invite-registration',
        name: 'InviteRegistration',
        component: InviteRegistration
    },

    // Customer routes with sidebar layout
    {
        path: '/customer',
        component: UserLayoutWrapper,
        meta: { requiresAuth: true, roles: ['customer', 'admin'], hideNavigation: true },
        children: [
            {
                path: 'catalogue',
                name: 'Catalogue',
                component: Catalogue
            },
            {
                path: 'cart',
                name: 'Cart',
                component: Cart
            },
            {
                path: 'checkout',
                name: 'Checkout',
                component: () => import('../views/Checkout.vue')
            },
            {
                path: 'my-orders',
                name: 'MyOrders',
                component: MyOrders
            },
            {
                path: 'orders/:id',
                name: 'OrderDetails',
                component: OrderDetails,
                props: true
            },
            {
                path: 'profile',
                name: 'Profile',
                component: Profile
            }
        ]
    },

    // Legacy routes for backward compatibility (redirect to customer routes)
    {
        path: '/catalogue',
        redirect: '/customer/catalogue'
    },
    {
        path: '/cart',
        redirect: '/customer/cart'
    },
    {
        path: '/checkout',
        redirect: '/customer/checkout'
    },
    {
        path: '/my-orders',
        redirect: '/customer/my-orders'
    },
    {
        path: '/orders/:id',
        redirect: '/customer/orders/:id'
    },
    {
        path: '/profile',
        redirect: '/customer/profile'
    },

    // Admin routes with sidebar layout
    {
        path: '/admin',
        component: AdminLayoutWrapper,
        meta: { requiresAuth: true, roles: ['admin'], hideNavigation: true },
        children: [
            {
                path: '',
                name: 'AdminDashboard',
                component: AdminDashboard
            },
            {
                path: 'users',
                name: 'AdminUsers',
                component: AdminUsers
            },
            {
                path: 'products',
                name: 'AdminProducts',
                component: AdminProducts
            },
            {
                path: 'labels',
                name: 'AdminLabels',
                component: AdminLabels
            },
            {
                path: 'orders',
                name: 'AdminPendingOrders',
                component: AdminPendingOrders
            },
            {
                path: 'workshop',
                name: 'AdminWorkshopJobs',
                component: AdminWorkshopJobs
            },
            {
                path: 'invites',
                name: 'AdminInvites',
                component: AdminInvites
            }
        ]
    },

    // Staff routes
    {
        path: '/staff/orders',
        name: 'StaffOrders',
        component: StaffOrders,
        meta: { requiresAuth: true, roles: ['admin', 'staff'] }
    },

    // Workshop routes with layout
    {
        path: '/workshop',
        component: WorkshopLayout,
        meta: { requiresAuth: true, roles: ['admin', 'workshop'], hideNavigation: true },
        children: [
            {
                path: '',
                redirect: '/workshop/jobs'
            },
            {
                path: 'jobs',
                name: 'WorkshopJobs',
                component: WorkshopJobs
            },
            // No profile page (workshop logins are managed by the admin), so
            // /workshop/profile and any other unknown workshop path go to the jobs board
            {
                path: ':pathMatch(.*)*',
                redirect: '/workshop/jobs'
            }
        ]
    },

    // Catch all route
    {
        path: '/:pathMatch(.*)*',
        redirect: '/'
    }
]

const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition
        } else {
            return { top: 0 }
        }
    }
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
    const userStore = useUserStore()

    // Skip guard for landing page and secret routes
    if (to.path === '/' || to.name === 'SecretProductDetail') {
        next()
        return
    }

    // Check if route requires authentication
    if (to.meta.requiresAuth) {
        // Ensure user store is initialized
        if (!userStore.isInitialized) {
            await userStore.initialize()
        }

        // Check if user is authenticated
        if (!userStore.isAuthenticated) {

            next('/')
            return
        }

        // Check role-based access; workshop users go back to their jobs board
        if (to.meta.roles && !to.meta.roles.includes(userStore.user?.role)) {
            next(userStore.user?.role === 'workshop' ? '/workshop/jobs' : '/')
            return
        }
    }

    next()
})

export default router 